import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, fmtMoney } from './AcademyShared';

const STATUSES = ['active', 'inactive', 'archived'];
const DELIVERY_MODES = ['onsite', 'online', 'hybrid'];

export default function AcademyBootcampTypes() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [companyCode, setCompanyCode] = useState('');
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/academy/bootcamp-types?per_page=50`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    fetch(`${API}/companies/${companyId}/academy/categories?per_page=200&status=active`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(j => setCategories(Array.isArray(j.data) ? j.data : [])).catch(() => setCategories([]));
  }, [companyId]);
  useEffect(() => {
    fetch(`${API}/companies/${companyId}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : {})
      .then(data => setCompanyCode(String(data.company?.code || data.code || '').trim()))
      .catch(() => setCompanyCode(''));
  }, [companyId]);

  const changeStatus = async (id, newStatus) => {
    await fetch(`${API}/companies/${companyId}/academy/bootcamp-types/${id}/status`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    fetchList();
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this bootcamp type?')) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/bootcamp-types/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🏕️ Bootcamp Types</h2>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Bootcamp Type</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input style={{ ...inputStyle, width: 220 }} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...inputStyle, width: 160 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Code', 'Name', 'Category', 'Duration', 'Levels', 'Price', 'Cohorts', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No bootcamp types</td></tr>}
              {list.map(bt => (
                <tr key={bt.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{bt.bootcamp_type_code}</td>
                  <td style={tdStyle}>{bt.name}</td>
                  <td style={tdStyle}>{bt.category?.name || '—'}</td>
                  <td style={tdStyle}>{bt.duration_weeks ? `${bt.duration_weeks}w` : '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{bt.total_levels}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtMoney(bt.default_price)}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{bt.cohorts_count ?? 0}</td>
                  <td style={tdStyle}><Badge value={bt.status} /></td>
                  <td style={tdStyle}>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setEditing(bt)}>Edit</button>
                    {bt.status === 'active' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(bt.id, 'inactive')}>Deactivate</button>}
                    {bt.status === 'inactive' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(bt.id, 'active')}>Activate</button>}
                    {bt.status !== 'archived' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(bt.id, 'archived')}>Archive</button>}
                    <button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeItem(bt.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <BootcampTypeModal companyId={companyId} companyCode={companyCode} categories={categories} item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />}
    </div>
  );
}

function BootcampTypeModal({ companyId, companyCode, categories, item, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({
    category_id: item.category_id || '', name: item.name || '', description: item.description || '',
    duration_weeks: item.duration_weeks || '', total_levels: item.total_levels || 1,
    default_price: item.default_price || 0, delivery_mode: item.delivery_mode || 'onsite',
    specialties: Array.isArray(item.specialties) ? item.specialties.join(', ') : '',
  });
  const [saving, setSaving] = useState(false);
  const selectedCategory = categories.find(category => String(category.id) === String(form.category_id));
  const codePrefix = selectedCategory?.category_code || '';

  const save = async () => {
    if (!form.name.trim() || !form.category_id) { alert('Name and Category are required'); return; }
    if (isNew && !form.code_suffix?.trim()) { alert('Enter the bootcamp type code suffix.'); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/companies/${companyId}/academy/bootcamp-types` : `${API}/companies/${companyId}/academy/bootcamp-types/${item.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, specialties: form.specialties.split(',').map(value => value.trim()).filter(Boolean) }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{isNew ? 'New Bootcamp Type' : 'Edit Bootcamp Type'}</h3>
        <FormRow label="Category *">
          <select style={inputStyle} value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
            <option value="">Select category</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </FormRow>
        <FormRow label="Bootcamp Type Code *">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>{codePrefix ? `${codePrefix}-` : 'Select a category to get its code'}</span>
            <input aria-label="Code suffix" style={{ ...inputStyle, flex: '1 1 140px' }} value={form.code_suffix || ''} onChange={e => setForm({ ...form, code_suffix: e.target.value })} placeholder={isNew ? 'Enter the last part, e.g. ENG' : 'Leave blank to keep existing code'} />
          </div>
          {!isNew && <small>Current code: {item.bootcamp_type_code}</small>}
        </FormRow>
        <FormRow label="Name *">
          <input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </FormRow>
        <FormRow label="Description">
          <textarea style={{ ...inputStyle, minHeight: 60 }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
        </FormRow>
        <FormRow label="Required Instructor Specialties">
          <input style={inputStyle} value={form.specialties} onChange={e => setForm({ ...form, specialties: e.target.value })} placeholder="For example: UI/UX, Graphic Design" />
          <small>Separate specialties with commas. Matching instructors will be suggested for cohorts of this type.</small>
        </FormRow>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormRow label="Duration (weeks)">
            <input type="number" style={inputStyle} value={form.duration_weeks} onChange={e => setForm({ ...form, duration_weeks: e.target.value })} />
          </FormRow>
          <FormRow label="Total Levels">
            <input type="number" min="1" style={inputStyle} value={form.total_levels} onChange={e => setForm({ ...form, total_levels: e.target.value })} />
          </FormRow>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormRow label="Default Price">
            <input type="number" step="0.01" style={inputStyle} value={form.default_price} onChange={e => setForm({ ...form, default_price: e.target.value })} />
          </FormRow>
          <FormRow label="Delivery Mode">
            <select style={inputStyle} value={form.delivery_mode} onChange={e => setForm({ ...form, delivery_mode: e.target.value })}>
              {DELIVERY_MODES.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </FormRow>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
